import { useEffect, useState } from 'react';

import { useActivityClient, ActivityItem } from '@atlassian/recent-work-client';

import { useUserContext } from '@/components/user-context/user-context-provider';
import { useBxpWebStargateClient } from '@/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client';

const GRAPHQL_GATEWAY_URL =
	process.env['NODE_ENV'] === 'production'
		? 'https://www.atlassian.com/gateway/api/graphql'
		: 'https://wac.stg.internal.atlassian.com/gateway/api/graphql';

const START_URL =
	process.env['NODE_ENV'] === 'production'
		? 'https://start.atlassian.com'
		: 'https://start.stg.atlassian.com';

const constructLoginUrl = (url: string) => {
	const loginUrl = new URL(url);
	loginUrl.pathname = '/login.jsp';
	const searchParam = new URLSearchParams();
	const browseUrl = new URL(loginUrl);
	browseUrl.pathname = '/browse';
	searchParam.append('os_destination', browseUrl.href);
	loginUrl.search = searchParam.toString();
	return loginUrl;
};

export interface UserProfileExistingSites {
	cloudId: string;
	displayName: string;
	url: string;
	hasAdminAccess: boolean;
	siteCreatedAt: number;
	products: {
		productName: string;
		productKey: string;
		productEdition: string;
	}[];
}

export const filterAvailableJiraSites = (
	sites: UserProfileExistingSites[] | undefined,
): UserProfileExistingSites[] => {
	if (sites) {
		const filteredSites =
			sites.filter((site) => {
				return site.products.some((product) => {
					return product.productKey.includes('jira-software.ondemand');
				});
			}) || [];

		return filteredSites;
	} else {
		return [];
	}
};

export const useRecentJiraSite = () => {
	const { profile, user } = useUserContext();

	const splitName = user.name?.split(' ');

	useBxpWebStargateClient();
	const activityClient = useActivityClient(
		'v3',
		'www.atlassian.com',
		undefined,
		GRAPHQL_GATEWAY_URL,
	);

	const availableJiraSites = filterAvailableJiraSites(profile.attributes?.existingSites);

	const hasMultipleJiras = availableJiraSites.length > 1;

	const [siteName, setSiteName] = useState('');
	const [siteUrl, setSiteUrl] = useState('');
	const [siteInitials, setSiteInitials] = useState('');
	const [cloudSiteId, setCloudSiteId] = useState<string | undefined>();
	const [recentWorkUrl, setRecentWorkUrl] = useState<string | undefined>();
	const [isResolvingSite, setIsResolvingSite] = useState(true);

	useEffect(() => {
		const promises = [];
		let mostRecentActivity: ActivityItem | undefined;
		const fetchActivitiesPromise = activityClient
			.fetchActivities(
				[50, 125, 250],
				{
					limit: 5,
					products: ['jira'],
				},
				['workedOn', 'viewed'],
			)
			.then((data) => {
				const mostRecentlyViewed = data.viewed[0];
				const mostRecentlyWorkedOn = data.workedOn[0];

				if (mostRecentlyViewed && mostRecentlyWorkedOn) {
					mostRecentActivity =
						new Date(mostRecentlyViewed.timestamp) > new Date(mostRecentlyWorkedOn.timestamp)
							? mostRecentlyViewed
							: mostRecentlyWorkedOn;
				} else if (mostRecentlyWorkedOn) {
					mostRecentActivity = mostRecentlyWorkedOn;
				} else if (mostRecentlyViewed) {
					mostRecentActivity = mostRecentlyViewed;
				}
			})
			.catch((error) => console.log(error));
		promises.push(fetchActivitiesPromise);

		Promise.allSettled(promises).then(() => {
			if (mostRecentActivity) {
				const loginUrl = constructLoginUrl(mostRecentActivity.object.url);
				setSiteUrl(loginUrl.href);
				setSiteName(loginUrl.hostname.split('.')[0] || '');
				setSiteInitials(
					loginUrl.hostname.slice(0, 1).toUpperCase() + loginUrl.hostname.slice(1, 2),
				);
				setCloudSiteId(mostRecentActivity.object.rootContainerIdentifier.id);
				setRecentWorkUrl(mostRecentActivity.object.url);
			} else if (availableJiraSites[0]) {
				const loginUrl = constructLoginUrl(availableJiraSites[0].url);
				setSiteUrl(loginUrl.href);
				setSiteName(loginUrl.hostname.split('.')[0] || '');
				setSiteInitials(
					loginUrl.hostname.slice(0, 1).toUpperCase() + loginUrl.hostname.slice(1, 2),
				);
				setCloudSiteId(availableJiraSites[0].cloudId);
				setRecentWorkUrl(undefined);
			} else {
				setSiteUrl(START_URL);
				setSiteName('');
				setRecentWorkUrl(undefined);
			}
			setIsResolvingSite(false);
		});
	}, []);

	return {
		splitName,
		hasMultipleJiras,
		siteName,
		siteUrl,
		siteInitials,
		cloudSiteId,
		recentWorkUrl,
		isResolvingSite,
	};
};
